import { call, callForm } from "../../util/action";

export function buildListUrl(descriptor, { page = 1, size = 25, search = "", filters = {}, ordering = "" } = {}) {
  const parts = [`page=${page}`];
  // The table owns the page size (its footer offers 25/50/100); 25 is the API's
  // StandardResultsSetPagination default and the fallback for any caller that omits it.
  parts.push(`page_size=${size}`);
  if (search) parts.push(`search=${encodeURIComponent(search)}`);
  if (ordering) parts.push(`ordering=${encodeURIComponent(ordering)}`);
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") parts.push(`${k}=${encodeURIComponent(v)}`);
  });
  return `${descriptor.endpoint}?${parts.join("&")}`;
}

export function fetchList(descriptor, params, cb) {
  call({ method: "GET", url: buildListUrl(descriptor, params) }, r => {
    if (Array.isArray(r)) return cb({ results: r, count: r.length });
    if (r && Array.isArray(r.results)) return cb({ results: r.results, count: r.count ?? r.results.length });
    cb({ results: [], count: 0 });
  });
}

export function fetchItem(descriptor, id, cb) {
  call({ method: "GET", url: `${descriptor.endpoint}${id}/` }, cb);
}

export function saveItem(descriptor, item, me, cb) {
  const isNew = !item.id;
  let payload = { ...item };
  if (isNew && typeof descriptor.injectOnCreate === "function") payload = descriptor.injectOnCreate(payload, me);
  (descriptor.fields || []).forEach(f => {
    if (f.type !== "entity") return;
    const v = payload[f.name];
    if (v == null) return;
    if (Array.isArray(v)) {
      payload[f.name] = v.map(x => (x && typeof x === "object" ? x.id ?? x.value ?? x : x));
    } else if (typeof v === "object") {
      payload[f.name] = v.id ?? v.value ?? v;
    }
  });

  // An `audience` field owns no key of its own: a survey targets exactly one of points /
  // point_groups / networks, so `AudienceField` writes into those three directly. `emptyValueFor`
  // still seeds a placeholder under the field's own name, which would otherwise be POSTed as a
  // phantom `audience: null` on every create.
  (descriptor.fields || []).forEach(f => {
    if (f.type === "audience") delete payload[f.name];
  });

  // Image fields: only resend a File (new upload) or "" (explicit clear).
  // An unchanged value is a URL string (or null) fetched from GET — the API can't accept that back.
  (descriptor.fields || []).forEach(f => {
    if (f.type !== "image") return;
    const v = payload[f.name];
    if (typeof File !== "undefined" && v instanceof File) return;
    if (v === "") return;
    delete payload[f.name];
  });

  const hasFile = Object.values(payload).some(v => typeof File !== "undefined" && v instanceof File);

  if (hasFile) {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (typeof File !== "undefined" && v instanceof File) fd.append(k, v);
      else if (Array.isArray(v)) v.forEach(item => fd.append(k, item));
      else if (typeof v === "object") fd.append(k, JSON.stringify(v));
      else fd.append(k, v);
    });
    callForm({ method: isNew ? "POST" : "PATCH", url: isNew ? descriptor.endpoint : `${descriptor.endpoint}${item.id}/`, post: fd }, cb);
    return;
  }

  call(
    {
      method: isNew ? "POST" : "PATCH",
      url: isNew ? descriptor.endpoint : `${descriptor.endpoint}${item.id}/`,
      post: payload,
    },
    cb
  );
}

export function deleteItem(descriptor, id, cb) {
  call({ method: "DELETE", url: `${descriptor.endpoint}${id}/` }, r => cb(!!r && r.status === 204));
}

export function patchItem(descriptor, id, data, cb) {
  call({ method: "PATCH", url: `${descriptor.endpoint}${id}/`, post: data }, cb);
}

// Normalizes a descriptor's deactivation config, whether declared via the new
// `deactivation` shape or the legacy `softDelete: true` shorthand.
export function getDeactivation(descriptor) {
  if (descriptor.deactivation) {
    const d = descriptor.deactivation;
    return {
      field: d.field,
      activeValue: d.activeValue,
      inactiveValue: "inactiveValue" in d ? d.inactiveValue : !d.activeValue,
      via: d.via || "patch",
    };
  }
  if (descriptor.softDelete) {
    return { field: "is_removed", activeValue: false, inactiveValue: true, via: "delete" };
  }
  return null;
}

export function isInactive(item, cfg) {
  return cfg ? item[cfg.field] !== cfg.activeValue : false;
}

/* `parseErrors` lives in `util/action.js` now — a 400 body is that file's business, and the
   merchandiser page needed the same reader. */
